import React from "react";
import { Box, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

export interface LogoProps {
  /** Logo variant to display */
  variant?: "horizontal" | "vertical" | "icon";
  /** Size of the logo */
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  /** Custom width override */
  width?: number | string;
  /** Custom height override */
  height?: number | string;
  /** Additional CSS classes */
  className?: string;
  /** Additional styles */
  style?: React.CSSProperties;
  /** Alt text for accessibility */
  alt?: string;
}

const Logo: React.FC<LogoProps> = ({
  variant = "icon",
  size = "md",
  width,
  height,
  className,
  style,
  alt = "Pagoda Labs Logo",
}) => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  // Size mapping for responsive design
  const sizeMap = {
    xs: { mobile: 24, desktop: 32 },
    sm: { mobile: 32, desktop: 40 },
    md: { mobile: 40, desktop: 60 },
    lg: { mobile: 48, desktop: 72 },
    xl: { mobile: 56, desktop: 96 },
    xxl: { mobile: 82, desktop: 120 },
  };

  // Get dimensions based on variant and size
  const getDimensions = () => {
    const baseSize = sizeMap[size];
    const actualSize = isMobile ? baseSize.mobile : baseSize.desktop;

    if (width || height) {
      return {
        width: width || "auto",
        height: height || "auto",
      };
    }

    switch (variant) {
      case "icon":
        return {
          width: actualSize,
          height: actualSize,
        };
      case "vertical":
        return {
          width: actualSize * 0.8,
          height: actualSize * 1.2,
        };
      case "horizontal":
      default:
        return {
          width: actualSize * 2.5,
          height: actualSize,
        };
    }
  };

  const dimensions = getDimensions();

  // Logo image source - you'll need to place the logo file in public/images/
  const horizontalLogoSrc = "/images/pagoda-logo-horizontal.png";
  const squareLogoSrc = "/images/pagoda-logo.png";

  return (
    <Box
      className={className}
      style={{
        ...dimensions,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      <img
        src={variant === "horizontal" ? horizontalLogoSrc : squareLogoSrc}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          transition: "all 0.2s ease",
        }}
      />
    </Box>
  );
};

export default Logo;
